import { MaterialReactTable } from "material-react-table";
import { useMemo } from "react";
import { useRef } from "react";
import { Fragment } from "react";
import { connect } from "react-redux";

import EditIcon from "@mui/icons-material/Edit";
import { Box } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";

import constants from "../../assets/constants";
import * as AdminSessionActions from "../../state/redux/adminSession";
import OmniGridButton from "../ui/omniGridButton";

const getValueByWeek = (row, week) => {
  try {
    return row.omniData.values[week].kpiValue;
  } catch (error) {
    return "-";
  }
};

const getStoplightColor = (targetValue, overRangeValue, underRangeValue) => {
  try {
    if (underRangeValue === 0) {
      if (targetValue > 0) {
        return "#f44336";
      } else {
        return "#F5C336";
      }
    }

    if (targetValue >= overRangeValue) {
      return "#3d9461";
    }

    if (targetValue <= underRangeValue) {
      return "#f44336";
    }

    return "#F5C336";
  } catch (error) {}
  return "#344336";
};

const getBusinessPillarColor = (row, theTheme) => {
  let businessPillarEnum = row.original.omniData.businessPillar;

  switch (businessPillarEnum) {
    case constants.empirePillarEnumes.leadership:
      return theTheme.palette.highlight.main;
    case constants.empirePillarEnumes.opertations:
      return theTheme.palette.error.main;
    case constants.empirePillarEnumes.marketing:
      return theTheme.palette.warning.main;
    case constants.empirePillarEnumes.finance:
      return theTheme.palette.success.main;
    case constants.empirePillarEnumes.personnel:
      return theTheme.palette.info.main;
    default:
      return theTheme.palette.primary.main;
  }
};

const calculateColumns = kpiArray => {
  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",

    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });

  let theReturn = [
    {
      accessorKey: "omniData.name",
      header: "Measurable",
      size: 200,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
      fontWeight: 700,
      sx: {
        borderRadius: "0",
        border: "1px dashed #e0e0e0",
      },
      Cell: ({ cell, row }) => (
        <Box
          component="span"
          sx={theme => ({
            backgroundColor: getBusinessPillarColor(row, theme),
            borderRadius: "0.25rem",
            color: "#222",
            fontWeight: 700,
            width: "100%",
            p: "0.5rem",
          })}
        >
          {cell.getValue()}
        </Box>
      ),
    },
    {
      accessorKey: "omniData.targetValue",
      header: "Target",
      size: 150,
      enableColumnFilterModes: false,
      enableSorting: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "right",
      },
      Cell: ({ cell, row }) => (
        <Box
          component="span"
          sx={theme => ({
            backgroundColor: getBusinessPillarColor(row, theme),
            borderRadius: "0.25rem",
            color: "#222",
            fontWeight: 700,
            width: "100%",
            p: "0.5rem",
          })}
        >
          {`${
            row.original.omniData.preUnitSymbol === "$"
              ? currencyFormatter.format(cell.getValue())
              : cell.getValue()
          }`}
        </Box>
      ),
    },
  ];
  let weekCount = 0;
  for (let index = 0; index < kpiArray.length; ++index) {
    weekCount = Math.max(weekCount, kpiArray[index].omniData.values.length);
  }

  for (let index = weekCount - 1; index >= 0; --index) {
    theReturn.push({
      accessorFn: row => `${getValueByWeek(row, index)}`, //accessorFn used to join multiple data into a single cell
      id: `omniData.values[${index}]`,
      header: `Week ${index + 1}`,

      size: 120,
      enableColumnFilterModes: false,
      enableSorting: false,

      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "right",
      },
      Cell: ({ cell, row }) => (
        <Box
          component="span"
          sx={theme => ({
            backgroundColor: getStoplightColor(
              cell.getValue(),
              row.original.omniData.overRangeValue,
              row.original.omniData.underRangeValue,
            ),
            borderRadius: "0.25rem",
            color: "#222",
            fontWeight: 700,
            width: "100%",
            p: "0.5rem",
          })}
        >
          {`${
            row.original.omniData.preUnitSymbol === "$"
              ? !isNaN(cell.getValue())
                ? currencyFormatter.format(cell.getValue())
                : cell.getValue()
              : cell.getValue()
          }${isNaN(cell.getValue()) ? "" : row.original.omniData.postUnitLabel}`}
        </Box>
      ),
    });
  }

  return theReturn;
};

export const SurveyKPI = props => {
  const { kpiODMs, onEditProvider = null } = props;

  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const tableContainerRef = useRef(null); //we can get access to the underlying TableContainer element and react to its scroll events

  const columns = useMemo(() => calculateColumns(kpiODMs), [kpiODMs]);

  if (kpiODMs == null) {
    return <Fragment></Fragment>;
  }

  return (
    <MaterialReactTable
      layoutMode="grid"
      columns={columns}
      data={kpiODMs}
      defaultColumn={{
        minSize: 20, //allow columns to get smaller than default
        maxSize: 9001, //allow columns to get larger than default
        size: 100, //make columns wider by default
        grow: false,
      }}
      initialState={{
        density: "compact",
        columnPinning: { left: ["omniData.name", "omniData.targetValue"], right: [] },
      }}
      enableColumnPinning={true}
      //header
      //   enableColumnActions={false}
      //   enableStickyHeader={true}
      enableStickyHeader={true}
      enableColumnActions={false}
      enableColumnFilters={false}
      enablePagination={false}
      enableSorting={false}
      //   filterFns={{
      //     customFilterFn: (row, id, filterValue) => {
      //       return row.getValue(id) === filterValue;
      //     },
      //   }}
      muiTableHeadCellFilterTextFieldProps={{
        sx: {
          "& .MuiInputBase-input": { padding: "6px" },

          "& .MuiInputBase-root": { paddingRight: "0px" },
          "& .MuiSelect-iconOutlined": { marginRight: "0.75rem" },
          width: "100%",
        },
        variant: "outlined",
      }}
      //row
      // enableRowActions={true}
      //   enableRowActions={false}
      //   enableRowSelection={false}
      //   getRowId={originalRow => originalRow._id}
      //   rowCount={rowCount}
      //   positionToolbarAlertBanner="bottom"
      //   muiTablePaginationProps={{
      //     rowsPerPageOptions: [5, 10, 20],
      //   }}
      muiTopToolbarProps={{
        sx: {
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
        },
      }}
      muiBottomToolbarProps={{
        sx: {
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.commonground.contrastText,
        },
      }}
      muiTablePaperProps={{
        sx: {
          boxShadow: "none",
        },
      }}
      muiTableContainerProps={{
        ref: tableContainerRef, //get access to the table container element
        sx: mobileLayout
          ? {
              minHeight: "564px",
              maxHeight: "564px",
              backgroundColor: theme.palette.commonground.main,
              color: theme.palette.commonground.main,
              borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.25)}`,
              borderRight: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.25)}`,
            }
          : {
              minHeight: "298px",
              maxHeight: "298px",
              backgroundColor: theme.palette.commonground.main,
              color: theme.palette.commonground.main,
              border: `solid 0.5px ${theme.palette.divider}`,
            },
      }}
      muiTableProps={{
        sx: {
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.commonground.main,
        },
      }}
      muiTableHeadCellProps={{
        sx: {
          backgroundColor: alpha(theme.palette.commonground.contrastText, 0.25),
          color: theme.palette.commonground.contrastText,
          justifyContent: "start",
        },
      }}
      renderRowActions={({ row }) => (
        <Box
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            gap: "2px",
            padding: 0,
          }}
        >
          <OmniGridButton
            key="edit"
            sx={{ ...(onEditProvider == null && { display: "none" }) }}
            onClick={() => {
              onEditProvider(row.original);
            }}
            icon={<EditIcon fontSize="small" />}
          />
        </Box>
      )}
      //   renderToolbarInternalActions={({ table }) => (
      //     <Box>
      //       {sorting.length > 0 && (
      //         <Tooltip title="Clear All Sorting" arrow>
      //           <IconButton
      //             sx={{ color: theme.palette.secondary.main }}
      //             onClick={() => {
      //               setSorting([]);
      //             }}
      //           >
      //             <ClearSortingIcon />
      //           </IconButton>
      //         </Tooltip>
      //       )}
      //       {columnFilters.length > 0 && (
      //         <Tooltip title="Clear All Filters" arrow>
      //           <IconButton
      //             sx={{ color: theme.palette.secondary.main }}
      //             onClick={() => {
      //               setColumnFilters([]);
      //             }}
      //           >
      //             <ClearIcon />
      //           </IconButton>
      //         </Tooltip>
      //       )}

      //       {/* eslint-disable react/jsx-pascal-case*/}
      //       {/* <MRT_ToggleGlobalFilterButton table={table} /> */}
      //       <MRT_ToggleFiltersButton table={table} />
      //       <MRT_ShowHideColumnsButton table={table} />
      //       <MRT_ToggleFullScreenButton table={table} />
      //       {/* eslint-enablereact/jsx-pascal-case */}
      //     </Box>
      //   )}
    />
  );
};

const mapStateToProps = state => ({
  awsGroup: state.userSession.awsGroup,
});

const mapDispatchToProps = {
  createReportRequest: AdminSessionActions.createReportRequest,
};
export default connect(mapStateToProps, mapDispatchToProps)(SurveyKPI);
